/**
 * SOLID Review
 * S — Good. The lobby's presentation: the table's code and its share link, the copy and the QR code, the start and its message, and
 *     the lobby's two reactions — its room loaded when it changes, and the self-heal poll while there is one.
 * O — Good. Another way to share is one more action.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Good. The lobby's snapshot and its four actions.
 * D — Strong. The Application's ports, the request contexts, the platform's clipboard, timers and address.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import { ClientJobModel } from './ClientJobModel';
import type { IConfigDepViewPlatform } from './platform/IConfigDepViewPlatform';
import type { ClientRequests } from '../client/ClientRequests';
import { resolveShareBase } from '../../shared/appConfig';
import { SERVER_REFUSED } from '../../application/ports/contracts';
import type { IPortClientStateView, TClientViewState } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

/** The lobby's three use cases carry no command: the table names the room. */
const LOAD_ROOM: TClientUseCaseInput<'loadLobbyRoom'> = {};
const POLL_TICK: TClientUseCaseInput<'lobbyPollTick'> = {};
const START: TClientUseCaseInput<'startGame'> = {};
/** What the player reads when the start failed for a reason that is not the server's answer. */
const UNEXPECTED = 'Unexpected error.';
/** The lobby's self-heal poll: a round that started unheard is caught within this long. */
const LOBBY_POLL_MS = 2500;
/** How long "Copied" shows. */
const COPIED_MS = 2000;

interface IStateLobbyOwn {
  readonly copied: boolean;
  readonly qrOpen: boolean;
  readonly error: string | null;
  readonly loading: boolean;
}

/** The lobby as its view renders it. */
export interface IStateLobby extends IStateLobbyOwn {
  readonly room: TClientViewState['room'];
  readonly localUser: TClientViewState['localUser'];
  /** With neither a room nor a seat this reads `undefined === undefined` — the host (§28.292's finding 3, pinned, not changed). */
  readonly isHost: boolean;
  /** The code a friend joins with, and its link. */
  readonly fullCode: string;
  readonly displayCode: string;
  readonly joinUrl: string;
}

export interface IConfigDepLobby {
  readonly useCases: Pick<IPortClientUseCases, 'startGame' | 'loadLobbyRoom' | 'lobbyPollTick'>;
  readonly view: IPortClientStateView;
  readonly requests: ClientRequests;
  readonly platform: Pick<IConfigDepViewPlatform, 'clipboard' | 'timers' | 'address'>;
}

const IDLE: IStateLobbyOwn = { copied: false, qrOpen: false, error: null, loading: false };
const COPIED: Partial<IStateLobbyOwn> = { copied: true };
const NOT_COPIED: Partial<IStateLobbyOwn> = { copied: false };
const QR_OPEN: Partial<IStateLobbyOwn> = { qrOpen: true };
const QR_CLOSED: Partial<IStateLobbyOwn> = { qrOpen: false };
const STARTING: Partial<IStateLobbyOwn> = { error: null, loading: true };
const STARTED: Partial<IStateLobbyOwn> = { loading: false };

/**
 * THE LOBBY (frontend-framework-oo P4, 2026-10-29, §28.296) — `components/game/LobbyPanel.tsx`'s state, effect and handlers, and the
 * poll its `useClientJob` ran (the binding is gone: the poll is a reaction here, after the room's load, in the component's order).
 * The share link is built on where the page is served from, once, as the module built it once.
 */
export class LobbyViewModel implements IServiceViewModel<IStateLobby> {
  private readonly state = new ObservableState<IStateLobbyOwn>(IDLE);
  private readonly shareBase: string;
  private readonly reactions: Reactions<IStateLobby>;
  private shown: {
    readonly own: IStateLobbyOwn; readonly room: IStateLobby['room']; readonly localUser: IStateLobby['localUser']; readonly lobby: IStateLobby;
  } | null = null;

  constructor(private readonly deps: IConfigDepLobby) {
    this.shareBase = resolveShareBase(deps.platform.address.location());
    this.reactions = new Reactions<IStateLobby>([
      { deps: (lobby) => [lobby.room?.roomId], run: (lobby) => { if (lobby.room?.roomId !== undefined) void deps.useCases.loadLobbyRoom(deps.requests.context(), LOAD_ROOM); } },
      ClientJobModel.reaction(deps.platform.timers, (lobby: IStateLobby) => [this.pollTick, LOBBY_POLL_MS, lobby.room?.roomId !== undefined] as const),
    ]);
  }

  private readonly pollTick = (): void => {
    void this.deps.useCases.lobbyPollTick(this.deps.requests.context(), POLL_TICK);
  };

  readonly copy = (): void => {
    const { clipboard, timers } = this.deps.platform;
    clipboard.write(this.getSnapshot().fullCode).then(() => {
      this.state.update(COPIED);
      timers.setTimeout(() => this.state.update(NOT_COPIED), COPIED_MS);
    }).catch(() => {});
  };

  readonly openQr = (): void => this.state.update(QR_OPEN);
  readonly closeQr = (): void => this.state.update(QR_CLOSED);

  readonly start = async (): Promise<void> => {
    const { room, localUser } = this.deps.view.getSnapshot();
    if (!room || !localUser) return;
    this.state.update(STARTING);
    try {
      const result = await this.deps.useCases.startGame(this.deps.requests.context(), START);
      if (!result.success) {
        const failed: Partial<IStateLobbyOwn> = { error: result.error.code === SERVER_REFUSED ? result.error.message : UNEXPECTED };
        this.state.update(failed);
      }
    } finally {
      this.state.update(STARTED);
    }
  };

  /** The lobby — the same object until its own state, the room or the seat changes. */
  getSnapshot(): IStateLobby {
    const own = this.state.get();
    const { room, localUser } = this.deps.view.getSnapshot();
    if (this.shown !== null && this.shown.own === own && this.shown.room === room && this.shown.localUser === localUser) return this.shown.lobby;
    const fullCode = room?.roomCode ?? room?.roomId ?? '';
    const lobby: IStateLobby = {
      ...own, room, localUser, isHost: room?.hostPlayerId === localUser?.playerId, fullCode, displayCode: fullCode.toUpperCase(),
      joinUrl: `${this.shareBase}/?join=${encodeURIComponent(fullCode)}`,
    };
    this.shown = { own, room, localUser, lobby };
    return lobby;
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveView = this.deps.view.subscribe(listener);
    return () => {
      leaveOwn();
      leaveView();
    };
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(_inputs: readonly unknown[], rendered: IStateLobby): void {
    this.reactions.stopChanged(rendered);
  }

  commit(_inputs: readonly unknown[], rendered: IStateLobby): void {
    this.reactions.startChanged(rendered);
  }
}
